import React, { useState, useEffect, useMemo, useCallback } from "react"
import { FolderArchive, Plus, Search, Download, Trash2, AlertTriangle, Share2, Pencil } from "lucide-react"
import { AppShell } from "src/components/layout/AppShell"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { cn } from "src/utils/cn"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "src/components/ui/select"
import { useToast } from "src/components/ui/toast"

import { useDocuments, useDeleteDocument } from "src/features/documents/hooks/useDocuments"
import { UploadDocumentModal } from "src/features/documents/components/UploadDocumentModal"
import { EditDocumentModal } from "src/features/documents/components/EditDocumentModal"
import { ShareDocumentDrawer } from "src/features/documents/components/ShareDocumentDrawer"
import { DocumentIcon } from "src/features/documents/components/DocumentIcon"
import {
  SCOPE_TABS, CATEGORY_GROUPS, CATEGORY_LABELS, categoryLabel, formatSize, EXPIRY_BADGE,
} from "src/features/documents/constants"

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

function ScopeTabs({ value, onChange }) {
  return (
    <div role="tablist" className="flex items-center gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
      {SCOPE_TABS.map((tab) => {
        const active = value === tab.id
        return (
          <button
            key={tab.id} role="tab" type="button" aria-selected={active}
            onClick={() => onChange(tab.id)}
            className={cn(
              "shrink-0 rounded-lg px-3.5 py-2 text-[13px] font-medium transition-colors",
              active ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            )}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}

function ExpiryBadge({ status }) {
  const badge = EXPIRY_BADGE[status]
  if (!badge) return null
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${badge.className}`}>
      {badge.label}
    </span>
  )
}

export default function DocumentsPage() {
  const { toast } = useToast()
  const { data, fetch, loading } = useDocuments(null)
  const remove = useDeleteDocument()

  const [scope, setScope] = useState("all")
  const [category, setCategory] = useState("")
  const [searchRaw, setSearch] = useState("")
  const [uploadOpen, setUploadOpen] = useState(false)
  const [editTarget, setEditTarget] = useState(null)
  const [shareTarget, setShareTarget] = useState(null)
  const q = useDebounce(searchRaw)

  const activeTab = SCOPE_TABS.find((t) => t.id === scope)

  // Categories available for the current tab (drives both the filter and the
  // upload modal). "all" exposes the full taxonomy.
  const tabCategories = useMemo(() => {
    if (!activeTab?.group) return Object.keys(CATEGORY_LABELS)
    return CATEGORY_GROUPS[activeTab.group]
  }, [activeTab])

  const buildFilters = useCallback(() => {
    const filters = {}
    if (activeTab?.documentableType) filters.documentable_type = activeTab.documentableType
    if (category) filters.category = category
    return filters
  }, [activeTab, category])

  const reload = useCallback(() => fetch(buildFilters()), [fetch, buildFilters])
  useEffect(() => { reload() }, [reload])

  // Reset category when switching to a tab that doesn't include it.
  useEffect(() => {
    if (category && !tabCategories.includes(category)) setCategory("")
  }, [tabCategories]) // eslint-disable-line react-hooks/exhaustive-deps

  const allDocs = data?.documents ?? []

  // Client-side search + transaction-tab filtering (transaction = lead/deal/booking docs).
  const docs = useMemo(() => {
    let list = allDocs
    if (activeTab?.id === "transactions") {
      list = list.filter((d) => ["Lead", "Deal", "Booking"].includes(d.documentable?.type))
    }
    if (q) {
      const needle = q.toLowerCase()
      list = list.filter((d) =>
        (d.title || "").toLowerCase().includes(needle) ||
        (d.file_name || "").toLowerCase().includes(needle) ||
        (d.documentable?.label || "").toLowerCase().includes(needle)
      )
    }
    return list
  }, [allDocs, activeTab, q])

  const expiringCount = allDocs.filter((d) => d.expiry_status === "expiring_soon" || d.expiry_status === "expired").length

  async function handleDelete(doc) {
    if (!window.confirm(`Delete "${doc.title || doc.file_name}"?`)) return
    try {
      await remove(doc.id)
      toast({ title: "Document deleted" })
      reload()
    } catch {
      toast({ variant: "destructive", title: "Could not delete document" })
    }
  }

  return (
    <AppShell>
      <div className="px-8 py-7 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
              <FolderArchive className="h-5 w-5 text-slate-700" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Documents</h1>
              <p className="text-[13px] text-slate-500">
                {data ? `${allDocs.length} document${allDocs.length !== 1 ? "s" : ""} in your library` : "Your document library"}
              </p>
            </div>
          </div>
          <Button className="gap-1.5" onClick={() => setUploadOpen(true)}>
            <Plus className="h-4 w-4" /> Upload
          </Button>
        </div>

        {/* Expiring banner */}
        {expiringCount > 0 && (
          <div className="flex items-center gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>{expiringCount} document{expiringCount !== 1 ? "s" : ""} expired or expiring within 30 days. Review and renew them to stay compliant.</span>
          </div>
        )}

        {/* Tabs + filters */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <ScopeTabs value={scope} onChange={setScope} />
          <div className="flex items-center gap-2">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-44"><SelectValue placeholder="All categories" /></SelectTrigger>
              <SelectContent>
                {tabCategories.map((c) => (
                  <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchRaw} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search documents…" className="w-56 pl-8"
              />
            </div>
          </div>
        </div>

        {/* Grid */}
        {loading && !data ? (
          <div className="py-12 text-center text-sm text-slate-400">Loading documents…</div>
        ) : docs.length === 0 ? (
          <div className="flex h-48 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-200 text-sm text-slate-400">
            <FolderArchive className="h-6 w-6" />
            No documents found
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {docs.map((doc) => (
              <div key={doc.id} className="group flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
                <div className="flex items-start gap-3">
                  <DocumentIcon contentType={doc.content_type} fileName={doc.file_name} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-slate-800">{doc.title || doc.file_name}</div>
                    <div className="truncate text-[11px] text-slate-400">{categoryLabel(doc.category)}</div>
                  </div>
                  <ExpiryBadge status={doc.expiry_status} />
                </div>
                <div className="flex items-center justify-between border-t border-slate-100 pt-2.5">
                  <div className="min-w-0 text-[11px] text-slate-400">
                    {doc.documentable?.label ? (
                      <span className="truncate">{doc.documentable.type} · {doc.documentable.label}</span>
                    ) : (
                      <span>{formatSize(doc.byte_size)}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <button onClick={() => setEditTarget(doc)}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-slate-700" title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    {doc.is_shareable_with_customer && (
                      <button onClick={() => setShareTarget(doc)}
                        className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-blue-50 hover:text-blue-600" title="Share">
                        <Share2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {doc.url && (
                      <a href={doc.url} target="_blank" rel="noopener noreferrer"
                        className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-slate-700" title="Download">
                        <Download className="h-3.5 w-3.5" />
                      </a>
                    )}
                    <button onClick={() => handleDelete(doc)}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600" title="Delete">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <UploadDocumentModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        scope={null}
        categories={tabCategories}
        onUploaded={reload}
      />

      <EditDocumentModal
        document={editTarget}
        open={!!editTarget}
        onOpenChange={(next) => { if (!next) setEditTarget(null) }}
        categories={tabCategories}
        onUpdated={reload}
      />

      <ShareDocumentDrawer
        document={shareTarget}
        open={!!shareTarget}
        onOpenChange={(next) => { if (!next) setShareTarget(null) }}
      />
    </AppShell>
  )
}
